import type { TokenCredential } from "@azure/core-auth";
import { mintInstallationToken } from "./mint.js";

const ARM_SCOPE = "https://management.azure.com/.default";
const FOUNDRY_API = "2025-04-01-preview";

/**
 * Mint a fresh installation token + PATCH the Foundry CustomKeys project
 * connection so the worker invoke that follows finds a current Authorization
 * value in `properties.credentials.keys.Authorization`.
 *
 * The F1 attachment shape is preserved verbatim — Foundry's MCP attachment
 * (tools[type:mcp] with project_connection_id) reads the connection's keys
 * at invoke time; we just keep refreshing them under the same connection
 * name. credentialRef → connection name (no change from F1).
 *
 * Cache lives in mintInstallationToken — when cached, we skip the GitHub
 * round-trip BUT we also skip the PATCH (the connection was already PATCHed
 * with the same token on the cache-write call). Cache eviction (expiry or
 * test reset) triggers a fresh mint + PATCH.
 *
 * @throws on any non-2xx from GitHub or Foundry (error message carries
 *         status + body excerpt for operator diagnosis).
 */
export async function rotateConnectionAuth(args: {
  credential: TokenCredential;
  kvUri: string;
  projectArmId: string; // /subscriptions/.../projects/<proj>
  connectionName: string;
  installationId: string;
  repository: string;
}): Promise<{ rotatedAt: Date; expiresAt: Date }> {
  // mintInstallationToken returns cached value if fresh — fast path.
  // We track whether the token came from cache to decide whether to PATCH.
  const { getCachedToken, putCachedToken } = await import("./cache.js");
  const before = getCachedToken(args.installationId, args.repository);
  const minted = await mintInstallationToken({
    credential: args.credential,
    kvUri: args.kvUri,
    installationId: args.installationId,
    repository: args.repository,
  });
  const cacheHit = before !== null && before.token === minted.token;

  if (!cacheHit) {
    // Cache miss → we have a NEW token → PATCH it into Foundry.
    const armTokenResp = await args.credential.getToken(ARM_SCOPE);
    if (!armTokenResp?.token) {
      throw new Error("rotateConnectionAuth: failed to acquire ARM management token");
    }
    const url = `https://management.azure.com${args.projectArmId}/connections/${args.connectionName}?api-version=${FOUNDRY_API}`;
    const res = await fetch(url, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${armTokenResp.token}`,
        "Content-Type": "application/json",
      },
      // Foundry's connection resource is polymorphic on authType; the PATCH
      // deserializes as a full shape (NOT a shallow merge), so omitting the
      // discriminator returns 400 "Missing discriminator property [AuthType]".
      // Mirror the same auth shape the initial PUT in brain-link.ts uses.
      body: JSON.stringify({
        properties: {
          authType: "CustomKeys",
          category: "CustomKeys",
          credentials: { keys: { Authorization: `Bearer ${minted.token}` } },
        },
      }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(
        `rotateConnectionAuth: HTTP ${String(res.status)} on PATCH ${url}\n${text.slice(0, 500)}`,
      );
    }
    // Ensure the cache reflects our PATCH (mintInstallationToken cached it,
    // but this is a belt-and-suspenders refresh — keep test contract clear).
    putCachedToken(args.installationId, args.repository, minted.token, minted.expiresAt);
  }

  return { rotatedAt: new Date(), expiresAt: minted.expiresAt };
}
